import type React from "react";

import { useCallback } from "react";
import { useEffect, useState } from "react";

import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useWindowSize } from "@/features/request/feedback-tool/feedbackTool/hooks/useWindowSize.tsx";
import { HEADER_HEIGHT } from "@/features/request/feedback-tool/feedbackTool/utils";

export function useImage(
  imgRef: null | React.MutableRefObject<HTMLImageElement | null>,
  isPinchActiveRef?: React.MutableRefObject<boolean>,
) {
  const { divImgRef, imageOnly, img, setFeedBack, setZoom, size, zoom } =
    useFeedbackTool();
  const { height: screenHeight, width: screenWidth } = useWindowSize();
  const [animationZoom, setAnimationZoom] = useState("");
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const image = imgRef?.current;
    const container = divImgRef.current;

    if (size === "FULL" && image && container) {
      const divHeight = imageOnly
        ? screenHeight - HEADER_HEIGHT
        : container.offsetHeight;
      const divWidth = imageOnly ? screenWidth : container.offsetWidth;
      const proportion = image.naturalHeight / image.naturalWidth;

      if (image.naturalHeight > divHeight) {
        return setZoom(Math.min(divHeight / proportion, divWidth));
      }

      if (image.naturalWidth > divWidth) {
        return setZoom(divWidth);
      }

      setZoom(image?.naturalWidth);
    }
  }, [size, divImgRef, imgRef, setZoom, screenWidth, screenHeight, imageOnly]);

  useEffect(() => {
    if (size === "FULL") {
      setTimeout(() => setAnimationZoom("all 0.3s ease-in-out"), 700);
    } else {
      setAnimationZoom("");
    }
  }, [size]);

  const recalculateFeedbackPositions = useCallback(
    (imageEl: HTMLImageElement) => {
      setIsResizing(true);
      setFeedBack(
        (prev) =>
          prev?.map((item) => {
            if (item.imgId === img.id) {
              return {
                ...item,
                xPosition: Number(item.x) * imageEl.clientWidth,
                yPosition: Number(item.y) * imageEl.clientHeight,
              };
            }

            return item;
          }) || null,
      );
      setIsResizing(false);
    },
    [img.id, setFeedBack],
  );

  useEffect(() => {
    const imgR = imgRef?.current;

    if (!imgR) return;

    const handleResize = debounce(() => {
      recalculateFeedbackPositions(imgR);
    }, 100);

    const observer = new ResizeObserver(() => {
      if (isPinchActiveRef?.current) return;

      setIsResizing(true);
      handleResize();
    });

    observer.observe(imgR);

    return () => {
      observer.disconnect();
    };
  }, [img.id, size, zoom, imgRef, recalculateFeedbackPositions]);

  return { animationZoom, isResizing };
}

function debounce<T extends (...args: string[]) => void>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}
